/**
 * Helper Class for creating table elements, with input fields etc.
 * Extends The Table Helper Class.
 */
 export default class TableElements {
    /**
     * @description Appending cell elements to table
     * @param {Object} params element object
     * @returns void
     */
    appendElements(params) {
        // Icons
        for (let type of params.icons) {
            params.cell.appendChild(
                this.addCellElements({
                    elementType: type,
                    id: params.dataId,
                })
            );
        }

        // Input field
        params.cell.appendChild(
            this.addCellElements({
                elementType: 'input',
                tableIndex: params.tableIndex,
                cellData: params.data,
                id: params.dataId,
                type: typeof params.tableProps !== undefined ? params.inputType : 'text',
                width: typeof params.tableProps !== undefined ? params.inputWidth : '90%',
                step: typeof params.tableProps !== undefined ? params.inputStep : false,
                stepValue: typeof params.tableProps !== undefined ? params.inputStepValue : 'any',
                initValue: typeof params.tableProps !== undefined ? params.initValue : '',
            })
        );

        // Span Text
        params.cell.appendChild(
            this.addCellElements({
                elementType: 'span',
                tableIndex: params.tableIndex,
                cellData: params.data,
            })
        );
    }

    /**
     * @description Adding cell elements
     * @param {Object} params element attributes
     * @returns element table element, icons, span, input
     */
    addCellElements(params) {
        let element = {};
        switch (params.elementType) {
            case 'iEdit':
                element = document.createElement('i');
                element.setAttribute('data-icon-after', `edit`);
                element.setAttribute('class', 'hidden td_edit');
                element.setAttribute('id', `icon_edit_${params.id}`);
                break;

            case 'iSave':
                element = document.createElement('i');
                element.setAttribute('data-icon-after', `save`);
                element.setAttribute('class', 'hidden saveInput');
                element.setAttribute('id', `icon_save_${params.id}`);
                break;

            case 'iUndo':
                element = document.createElement('i');
                element.setAttribute('data-icon-after', `undo`);
                element.setAttribute('class', 'hidden td_undo');
                element.setAttribute('id', `icon_undo_${params.id}`);
                break;

            case 'input':
                element = document.createElement('input');
                element.setAttribute(
                    'id',
                    `input_${params.tableIndex.rowIndex}_${params.tableIndex.columnIndex}`
                );
                element.setAttribute('data-init-value', params.initValue);
                element.setAttribute('value', params.initValue);
                element.setAttribute('data-id', params.id);
                element.setAttribute('class', 'hidden td_input');

                if ('type' in params) {
                    element.setAttribute('type', params.type);
                }

                if ('width' in params) {
                    element.setAttribute('style', `width: ${params.width}`);
                }

                if ('step' in params && params.step) {
                    element.setAttribute('step', params.stepValue);
                }

                break;

            case 'span':
                element = document.createElement('span');
                element.setAttribute('class', 'td_span');
                element.setAttribute(
                    'id',
                    `span_${params.tableIndex.rowIndex}_${params.tableIndex.columnIndex}`
                );
                element.innerText = params.cellData;
                break;
        }
        return element;
    }

    /**
     * @description Change edit icon and activate save button.
     * Adding/Removing css className on element.
     * @param {Object} evt event
     * @returns void
     */
    prepareSave(evt) {
        if (evt.parentNode.querySelector('[data-icon-after="save"]') === null) {
            return false;
        }

        if (evt.value !== evt.getAttribute('data-init-value')) {
            evt.parentNode.querySelector('[data-icon-after="undo"]').classList.add('hidden');
            evt.parentNode.querySelector('[data-icon-after="save"]').classList.remove('hidden');
            evt.parentNode.querySelector('[data-icon-after="edit"]').classList.add('hidden');
            evt.parentNode.querySelector('input').classList.add('changed');
        } else {
            evt.parentNode.querySelector('[data-icon-after="save"]').classList.add('hidden');
            evt.parentNode.querySelector('[data-icon-after="edit"]').classList.remove('hidden');
            evt.parentNode.querySelector('input').classList.add('changed');
        }

        if (document.querySelectorAll('#saveAll').length > 0) {
            document.querySelector('#saveAll').classList.remove('hidden');
        } else {
            document.querySelector('#saveAll').classList.add('hidden');
        }
    }

    /**
     * @description Deactivate input and activate span.
     * @param {string} id element to deactivate
     * @returns void
     */
    deActivateElement(id) {
        document.querySelector(`#${id} input`).classList.add('hidden');
        document.querySelector(`#${id} input`).blur();
        document.querySelector(`#${id} span`).classList.remove('hidden');
        document.querySelector(`#${id}`).classList.remove('editCell');
    }

    /**
     * @description Activate input and deactivate span.
     * @param {Object} targetElement event target element
     * @returns void
     */
    activateElement(targetElement) {
        targetElement.querySelector('input').classList.remove('hidden');
        targetElement.querySelector('input').focus();
        targetElement.querySelector('span').classList.add('hidden');
        targetElement.classList.add('editCell');
    }

    /**
     * @description Undo changes and reset to init value (Except edit will change to undo)
     * @param {Object} item element || event
     * @param string mode single || all
     * @returns void
     */
    resetInitValue(item, mode) {
        if (mode === 'single') {
            item.target.setAttribute('data-icon-after', 'save');
            item.target.parentNode
                .querySelector('[data-icon-after="save"]')
                .classList.add('hidden');

            let icon = this.checkUndoValues(item.target.parentNode.querySelector('input'));

            item.target.parentNode
                .querySelector(`[data-icon-after="${icon}"]`)
                .classList.remove('hidden');
        }

        if (mode === 'all') {
            for (let saveIcon of document.querySelectorAll(
                '.editable i[data-icon-after="cloud_done"]'
            )) {
                saveIcon.setAttribute('data-icon-after', 'save');
                saveIcon.parentNode
                    .querySelector('[data-icon-after="save"]')
                    .classList.add('hidden');

                let icon = this.checkUndoValues(saveIcon.parentNode.querySelector('input'));

                saveIcon.parentNode
                    .querySelector(`[data-icon-after="${icon}"]`)
                    .classList.remove('hidden');

                document.querySelector('#saveAll i').setAttribute('data-icon-after', 'save');
                document.querySelector('#saveAll').classList.remove('savingDone');
            }
        }
    }

    /**
     * @description Simple check if values match initial value
     * @param {Object} element input element
     * @returns string undo || edit icon
     */
    checkUndoValues(element) {
        if (element.value !== element.getAttribute('data-init-value')) {
            return 'undo';
        } else {
            return 'edit';
        }
    }

    /**
     * @description Restore cell and cell elements to default state
     * @param {Object} params icon element and mode for single || all, response
     * @returns void
     */
    restoreElements(params) {
        params.element.target.parentNode.classList.remove('editCell');
        document.querySelector('input').blur();

        if (params.mode === 'all') {
            for (let item of document.querySelectorAll(
                '.saveInput[data-icon-after="cloud_queue"]'
            )) {
                item.setAttribute('data-icon-after', 'cloud_done');

                this.delayedReset(item, params.mode);
                this.deActivateElement(item.parentNode.getAttribute('id'));
            }
        }

        if (params.mode === 'single') {
            params.element.target.parentNode
                .querySelector('.saveInput')
                .setAttribute('data-icon-after', 'cloud_done');

            this.delayedReset(params.element, params.mode);
            this.deActivateElement(params.element.target.parentNode.getAttribute('id'));
        }
    }

    /**
     * @description delay icon change to make it visible for user
     * @param {Object} element icon element to change
     * @param {string} mode single || all
     * @returns void
     */
    delayedReset(element, mode) {
        setTimeout(
            () => {
                this.resetInitValue(element, mode);
            },
            3000,
            this
        );
    }
}
